import WheelPicker from '@quidone/react-native-wheel-picker'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { Modal, Platform, TouchableOpacity, View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'

export type ModalWheelPickerItem<T> = {
  value: T
  label: string
}

type ModalWheelPickerProps<T> = {
  isVisible: boolean
  title: string
  selectedItem: T
  itemList: ModalWheelPickerItem<T>[]
  onSelect: (item: T) => void
  onClose: () => void
  keyExtractor?: (item: ModalWheelPickerItem<T>) => string
  visibleItemCount?: number
}

export function ModalWheelPicker<T>({
  isVisible,
  title,
  selectedItem,
  itemList,
  onSelect,
  onClose,
  keyExtractor = item => item.label,
  visibleItemCount = 5,
}: ModalWheelPickerProps<T>) {
  const styles = useStyles()
  const { theme } = useTheme()

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.pickerContainer}>
          <View style={styles.pickerHeader}>
            <Text h4>{title}</Text>
            <TouchableOpacity onPress={onClose}>
              <Icon name="close" size={24} color={theme.colors.grey3} />
            </TouchableOpacity>
          </View>
          <ItemPicker
            selectedItem={selectedItem}
            onItemSelect={value => {
              onSelect(value)
              onClose()
            }}
            itemList={itemList}
            keyExtractor={keyExtractor}
            visibleItemCount={visibleItemCount}
          />
        </View>
      </View>
    </Modal>
  )
}

type ItemPickerProps<T> = {
  selectedItem: T
  onItemSelect: (item: T) => void
  itemList: ModalWheelPickerItem<T>[]
  keyExtractor: (item: ModalWheelPickerItem<T>) => string
  visibleItemCount: number
}

export const ItemPicker = <T,>({
  selectedItem,
  onItemSelect,
  itemList,
  keyExtractor,
  visibleItemCount,
}: ItemPickerProps<T>) => {
  const styles = useStyles()

  if (Platform.OS === 'web') {
    return (
      <FlatList
        data={itemList}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[
              styles.itemRow,
              selectedItem === item.value && styles.selectedItemRow,
            ]}
            onPress={() => onItemSelect(item.value)}
          >
            <Text
              style={[
                styles.itemText,
                selectedItem === item.value && styles.selectedItemText,
              ]}
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        )}
        keyExtractor={item => keyExtractor(item)}
        showsVerticalScrollIndicator={true}
        style={styles.itemList}
        initialNumToRender={itemList.length}
      />
    )
  }

  return (
    <WheelPicker
      data={itemList}
      value={selectedItem}
      onValueChanged={({ item: { value } }) => onItemSelect(value)}
      itemTextStyle={styles.pickerItemText}
      overlayItemStyle={styles.wheelPickerItemContainer}
      visibleItemCount={visibleItemCount}
    />
  )
}

const useStyles = makeStyles(theme => ({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pickerContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: 10,
    width: '80%',
    padding: 20,
  },
  pickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  pickerItemText: {
    fontSize: 16,
    color: theme.colors.black,
  },
  wheelPickerItemContainer: {
    backgroundColor: 'gray',
  },
  selectorIcon: {
    marginLeft: 8,
  },
  selectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemList: {
    maxHeight: 200,
  },
  itemRow: {
    padding: 12,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey5,
  },
  selectedItemRow: {
    backgroundColor: theme.colors.primary,
  },
  itemText: {
    fontSize: 16,
    color: theme.colors.black,
  },
  selectedItemText: {
    color: theme.colors.white,
  },
}))
